import { Head } from "@inertiajs/react";
import { useMemo } from "react";

import { Button } from "@/components/ui/button";
import AuthLayout from "@/layouts/auth-layout";

interface AuthorizationParams {
  client_id: string;
  redirect_uri: string;
  state?: string | null;
  code_challenge: string;
  code_challenge_method: string;
  resource: string;
  scope: string;
  response_type: string;
}

interface Props {
  client_name: string;
  signed_in: boolean;
  user_email?: string | null;
  account_sign_in_url?: string | null;
  redirect_uri: string;
  scope: string;
  authorization: AuthorizationParams;
}

export default function OauthAuthorize({
  client_name,
  signed_in,
  user_email,
  account_sign_in_url,
  redirect_uri,
  scope,
  authorization,
}: Props) {
  const csrfToken = useMemo(
    () =>
      typeof document !== "undefined"
        ? document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
        : "",
    [],
  );

  const hiddenFields = () => (
    <>
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      {Object.entries(authorization).map(([name, value]) =>
        value ? <input key={name} type="hidden" name={name} value={String(value)} /> : null,
      )}
    </>
  );

  return (
    <AuthLayout
      title="Connect OG Pilot"
      description={
        signed_in
          ? `${client_name} wants to generate Open Graph images using your OG Pilot account.`
          : `${client_name} wants to use OG Pilot tools.`
      }
    >
      <Head title="Connect OG Pilot" />

      <div className="space-y-6">
        <div className="text-muted-foreground space-y-2 text-sm">
          {signed_in && user_email ? (
            <p>
              Signed in as <span className="text-foreground font-medium">{user_email}</span>. Each new image uses one
              credit from your plan.
            </p>
          ) : (
            <p>
              As a guest you can debug Open Graph tags, compress images, and extract color palettes. To generate images,
              sign in to an OG Pilot account with an active plan.
            </p>
          )}
          <p>This connector can start a generation for a page URL and wait until the image is ready.</p>
          <p>
            Returning to <span className="break-all">{redirect_uri}</span>
          </p>
          <p>Requested access: {scope}</p>
        </div>

        <div className="flex flex-col gap-3">
          {/* Native POST: Inertia <Form> sends application/json and cannot follow a 302 to claude.ai */}
          <form method="post" action="/oauth/authorize" className="flex flex-col gap-3">
            {hiddenFields()}
            <input type="hidden" name="approve" value="true" />
            <Button type="submit">Allow access</Button>
          </form>

          <form method="post" action="/oauth/authorize">
            {hiddenFields()}
            <input type="hidden" name="approve" value="false" />
            <Button type="submit" variant="outline">
              Deny
            </Button>
          </form>

          {!signed_in && account_sign_in_url ? (
            <p className="text-muted-foreground text-sm">
              Have an OG Pilot account?{" "}
              <a href={account_sign_in_url} className="text-foreground font-medium underline underline-offset-4">
                Use existing account
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </AuthLayout>
  );
}
